import { useState, type PropsWithChildren } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { logger } from "@/lib/logger";

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Unknown error";

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        throwOnError: false,
      },
      mutations: {
        retry: 1,
        throwOnError: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        logger.error("Query error", {
          queryKey: query.queryKey,
          error: getErrorMessage(error),
          meta: query.meta,
        });
      },
      onSuccess: (data, query) => {
        logger.debug("Query success", {
          queryKey: query.queryKey,
          dataCount: Array.isArray(data) ? data.length : 1,
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        logger.error("Mutation error", {
          mutationKey: mutation.options.mutationKey,
          error: getErrorMessage(error),
        });
      },
      onSuccess: (_data, variables, _context, mutation) => {
        logger.userAction("mutation_success", {
          mutationKey: mutation.options.mutationKey,
          variables,
        });
      },
    }),
  });

type QueryProviderProps = PropsWithChildren<{
  client?: QueryClient;
}>;

export const QueryProvider = ({ children, client }: QueryProviderProps) => {
  const [queryClient] = useState(() => client ?? createQueryClient());

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

export default QueryProvider;
